import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { OrganizationAuditLog, OrgAuditAction } from '../types/database'

interface AuditLogFilters {
  action?: OrgAuditAction | null
  startDate?: Date | null
  endDate?: Date | null
}

interface UseOrgAuditLogsReturn {
  logs: OrganizationAuditLog[]
  loading: boolean
  error: string | null
  totalCount: number
  hasMore: boolean
  filters: AuditLogFilters
  loadLogs: (organizationId: string, reset?: boolean) => Promise<void>
  loadMore: () => Promise<void>
  setFilters: (filters: AuditLogFilters) => void
  refresh: () => Promise<void>
}

const PAGE_SIZE = 50

export function useOrgAuditLogs(): UseOrgAuditLogsReturn {
  const [logs, setLogs] = useState<OrganizationAuditLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [filters, setFiltersState] = useState<AuditLogFilters>({})
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null)

  const loadLogs = useCallback(async (organizationId: string, reset = true) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentOrgId(organizationId)

      const currentOffset = reset ? 0 : offset

      // Load logs
      const { data, error: fetchError } = await supabase.rpc('get_org_audit_logs', {
        p_organization_id: organizationId,
        p_limit: PAGE_SIZE,
        p_offset: currentOffset,
        p_action: filters.action || null,
        p_start_date: filters.startDate?.toISOString() || null,
        p_end_date: filters.endDate?.toISOString() || null
      })

      if (fetchError) throw fetchError

      // Get total count
      const { data: countData, error: countError } = await supabase.rpc('count_org_audit_logs', {
        p_organization_id: organizationId,
        p_action: filters.action || null,
        p_start_date: filters.startDate?.toISOString() || null,
        p_end_date: filters.endDate?.toISOString() || null
      })

      if (countError) throw countError

      if (reset) {
        setLogs(data || [])
        setOffset(PAGE_SIZE)
      } else {
        setLogs(prev => [...prev, ...(data || [])])
        setOffset(prev => prev + PAGE_SIZE)
      }

      setTotalCount(countData || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [offset, filters])

  const loadMore = useCallback(async () => {
    if (currentOrgId && !loading && logs.length < totalCount) {
      await loadLogs(currentOrgId, false)
    }
  }, [currentOrgId, loading, logs.length, totalCount, loadLogs])

  const setFilters = useCallback((newFilters: AuditLogFilters) => {
    setFiltersState(newFilters)
    setOffset(0)
  }, [])

  const refresh = useCallback(async () => {
    if (currentOrgId) {
      await loadLogs(currentOrgId, true)
    }
  }, [currentOrgId, loadLogs])

  const hasMore = logs.length < totalCount

  return {
    logs,
    loading,
    error,
    totalCount,
    hasMore,
    filters,
    loadLogs,
    loadMore,
    setFilters,
    refresh
  }
}
